/**
 * Self-contained Lenis + Hero parallax.
 * Loaded via theme.liquid with manual ?v= bust (not bare content-hash alone).
 *
 * KEEP (do not gut when debugging cache):
 * - Lenis smoothWheel + lerp 0.085 (inertia after release)
 * - Own requestAnimationFrame loop (not gsap.ticker)
 * - Parallax: progress * height * lag * 0.7 on Lenis scroll
 * - Never use Lenis `duration` (slow→snap→stop)
 */
(function () {
  if (window.__SMOOTH_SCROLL_INIT_STARTED) {
    return;
  }
  window.__SMOOTH_SCROLL_INIT_STARTED = true;

  try {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  } catch (e) {}

  const DEFAULT_LAG = 0.55;
  const GSAP_MAX_RETRIES = 120;

  let lenisInstance = null;
  let scrollTriggerUpdateScheduled = false;
  const initializedHeroes = new WeakSet();
  const heroParallaxTriggers = new WeakMap();

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function qs(selector, context) {
    return (context || document).querySelector(selector);
  }

  function qsa(selector, context) {
    return Array.from((context || document).querySelectorAll(selector));
  }

  function scheduleScrollTriggerUpdate() {
    if (scrollTriggerUpdateScheduled || typeof ScrollTrigger === 'undefined') {
      return;
    }

    scrollTriggerUpdateScheduled = true;

    requestAnimationFrame(function () {
      scrollTriggerUpdateScheduled = false;

      if (typeof ScrollTrigger !== 'undefined') {
        ScrollTrigger.update();
      }
    });
  }

  function dispatchScrollEvent() {
    window.dispatchEvent(
      new CustomEvent('sf:scroll', {
        detail: { scroll: lenisInstance ? lenisInstance.scroll : window.scrollY },
      })
    );
  }

  function initLenis() {
    if (lenisInstance) {
      return lenisInstance;
    }

    if (prefersReducedMotion() || typeof Lenis === 'undefined' || typeof gsap === 'undefined') {
      return null;
    }

    const currentY = window.scrollY || window.pageYOffset || 0;

    lenisInstance = new Lenis({
      smoothWheel: true,
      syncTouch: false,
      touchMultiplier: 1.4,
      wheelMultiplier: 0.9,
      lerp: 0.085,
    });

    lenisInstance.scrollTo(currentY, { immediate: true });
    window.__lenis = lenisInstance;

    if (typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
      lenisInstance.on('scroll', scheduleScrollTriggerUpdate);
    }

    lenisInstance.on('scroll', dispatchScrollEvent);

    function raf(time) {
      lenisInstance.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return lenisInstance;
  }

  function getParallaxLag(hero) {
    const strength = Number(hero.style.getPropertyValue('--hero-parallax-strength'));
    const lagVar = Number(hero.style.getPropertyValue('--hero-parallax-lag'));

    if (Number.isFinite(lagVar) && lagVar > 0) {
      return Math.min(0.85, Math.max(0.2, lagVar));
    }

    if (!Number.isFinite(strength) || strength <= 0) {
      return DEFAULT_LAG;
    }

    return Math.min(0.85, Math.max(0.2, strength / 100));
  }

  function destroyParallax(hero) {
    const triggers = heroParallaxTriggers.get(hero);

    if (triggers) {
      triggers.forEach(function (trigger) {
        trigger.kill();
      });
      heroParallaxTriggers.delete(hero);
    }

    const parallaxTarget = qs('[data-hero-parallax]', hero);

    if (typeof gsap !== 'undefined' && parallaxTarget) {
      gsap.set(parallaxTarget, { clearProps: 'transform' });
    }

    initializedHeroes.delete(hero);
  }

  function createParallax(hero, parallaxTarget) {
    const lag = getParallaxLag(hero);
    const setY = gsap.quickSetter(parallaxTarget, 'y', 'px');

    gsap.set(parallaxTarget, { y: 0, force3D: true });

    function update() {
      const rect = hero.getBoundingClientRect();
      const range = Math.max(hero.offsetHeight, 1);
      const progress = Math.min(1, Math.max(0, -rect.top / range));
      setY(progress * hero.offsetHeight * lag * 0.7);
    }

    const lenis = lenisInstance || window.__lenis;
    if (lenis && typeof lenis.on === 'function') {
      lenis.on('scroll', update);
      update();
      return [
        {
          kill: function () {
            lenis.off('scroll', update);
          },
        },
      ];
    }

    gsap.ticker.add(update);
    update();
    return [
      {
        kill: function () {
          gsap.ticker.remove(update);
        },
      },
    ];
  }

  function initHeroInstance(hero) {
    if (initializedHeroes.has(hero)) {
      return;
    }

    if (prefersReducedMotion() || hero.dataset.parallax !== 'true') {
      return;
    }

    if (typeof gsap === 'undefined') {
      return;
    }

    const parallaxTarget = qs('[data-hero-parallax]', hero);

    if (!parallaxTarget) {
      return;
    }

    initializedHeroes.add(hero);

    const triggers = createParallax(hero, parallaxTarget);

    if (triggers.length) {
      heroParallaxTriggers.set(hero, triggers);
    }
  }

  function killStaleHeroTriggers() {
    if (typeof ScrollTrigger === 'undefined') {
      return;
    }

    qsa('[data-section-type="hero"]').forEach(function (hero) {
      const target = qs('[data-hero-parallax]', hero);

      ScrollTrigger.getAll().forEach(function (trigger) {
        if (trigger.trigger === hero || trigger.trigger === target) {
          trigger.kill();
        }
      });

      if (typeof gsap !== 'undefined' && target) {
        gsap.set(target, { clearProps: 'transform' });
      }
    });
  }

  function boot(retriesLeft) {
    if (typeof gsap === 'undefined' || typeof Lenis === 'undefined') {
      if (retriesLeft > 0) {
        requestAnimationFrame(function () {
          boot(retriesLeft - 1);
        });
      }
      return;
    }

    killStaleHeroTriggers();
    initLenis();
    qsa('[data-section-type="hero"]').forEach(initHeroInstance);

    window.__SMOOTH_SCROLL_BOOTED = true;
  }

  boot(GSAP_MAX_RETRIES);

  document.addEventListener('shopify:section:load', function (event) {
    qsa('[data-section-type="hero"]', event.target).forEach(function (hero) {
      destroyParallax(hero);
      initHeroInstance(hero);
    });
  });

  window.addEventListener('resize', function () {
    clearTimeout(window.__heroParallaxResizeTimer);
    window.__heroParallaxResizeTimer = setTimeout(function () {
      if (typeof ScrollTrigger !== 'undefined') {
        ScrollTrigger.refresh();
      }
    }, 150);
  });
})();
